import * as ReactDOM from 'react-dom';
import * as Sentry from "@sentry/react";
import { legacy_createStore as createStore, applyMiddleware, compose } from 'redux';
import { Provider } from 'react-redux';
import ReduxThunk from 'redux-thunk';
import { initReactI18next } from 'react-i18next';
import { reauthenticationMiddleware } from './middleware/reauthenticationMiddleware';
import { watchForUndoableEditorActions } from './middleware/watchForUndoableEditorActions';
import { routerMiddleware, ConnectedRouter } from 'connected-react-router';
import createSagaMiddleware from 'redux-saga';
import { makeAppReducer } from './reducers/makeAppReducer';
import { registerAllWithMiddleware } from './sagas';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { loginRequest } from './actions/sessionActions';
import { createBrowserHistory } from 'history';
import { App } from 'ui/shell/app/App';
import { WebSocketClient } from 'wss/WebSocketClient';
import { WebSocketProvider } from 'hooks/useWebSocket/useWebSocket';
import { websocketEnabler } from 'middleware/websocketEnabler';
import { SessionProvider } from 'hooks/useSession/useSession';
import { storeLoginUrl } from 'actions/shellActions';
import { ReloadPage } from 'ui/shell/reloadPage/ReloadPage';
import { sentryEnv, sentryRate, sentryRelease, sentryEnabled } from 'util/env';
import { loadLanguage } from 'lang/loadLanguage';

const history = createBrowserHistory();

if (sentryEnabled) {
    Sentry.init({
        dsn: "https://301b43c9d79640d8acd5c5e84de7367c@o1045918.ingest.sentry.io/6021663",
        integrations: [
            Sentry.reactRouterV5BrowserTracingIntegration({ history }),
        ],

        environment: sentryEnv,
        release: sentryRelease,
    
        // We recommend adjusting this value in production, or using tracesSampler for finer control.
        // Read: https://docs.sentry.io/platforms/javascript/guides/react/configuration/sampling/
        tracesSampleRate: sentryRate,
    });
}

const i18nPromise = i18next.use(LanguageDetector as any).use(initReactI18next as any).init({
    fallbackLng: 'en',
    load: 'languageOnly',
    initImmediate: false,
    debug: true,
    resources: {
        en: { translation: require('./lang/en/en.json') },
        es: { translation: require('./lang/es/es.json') },
        fr: { translation: require('./lang/fr/fr.json') },
        it: { translation: require('./lang/it/it.json') },
    },
});

// Require the Semantic UI javascript here because it requires jQuery (which is present on client but not server)
require('semantic-ui-site/site.js');
require('semantic-ui-form/form.js');
require('semantic-ui-api/api.js');
require('semantic-ui-transition/transition.js');
require('semantic-ui-dropdown/dropdown.js');

const sagaMiddleware = createSagaMiddleware();

const websocketClient = new WebSocketClient(/localhost/.test(window.location.host) ? 'localhost:8443' : window.location.host);

const storeWithMiddleware = (compose as any)(
    applyMiddleware(
        watchForUndoableEditorActions as any,
        sagaMiddleware,
        reauthenticationMiddleware as any,
        routerMiddleware(history as any),
        websocketEnabler(websocketClient) as any,
        ReduxThunk,
    ),
    (window as any).__REDUX_DEVTOOLS_EXTENSION__ ? (window as any).__REDUX_DEVTOOLS_EXTENSION__() : (f: any) => f
)(createStore);

const appReducer = makeAppReducer(history);
const store = storeWithMiddleware(appReducer);
    
registerAllWithMiddleware(sagaMiddleware);

void (async () => {
    await i18nPromise;
    
    const selectedLang = i18next.languages[0];
    const body = document.getElementsByTagName('body')[0];
    body.setAttribute('class', `lang-${selectedLang}`);
    loadLanguage(selectedLang);

    const { pathname, search } = window.location;
    const loginUrl = `${pathname}${search || ''}`;

    const rootElement = document.getElementById('root');

    function renderUI() {
        ReactDOM.render(
            <Sentry.ErrorBoundary fallback={ <ReloadPage/> }>
                <Provider store={ store }>
                    <ConnectedRouter history={ history }>
                        <SessionProvider>
                            <WebSocketProvider client={ websocketClient }>
                                <App/>
                            </WebSocketProvider>
                        </SessionProvider>
                    </ConnectedRouter>
                </Provider>
            </Sentry.ErrorBoundary>,
            rootElement
        );
    }

    // If the user landed on the reset password page then show the UI right away...
    // because there is no point trying to cookie log in if they're resetting their password.
    if (/reset-password/.test(loginUrl)) {
        renderUI();
        return;
    }

    // Capture the landing URL so we can redirect to it after successful login
    if (!/^\/login$/i.test(loginUrl)) {
        store.dispatch(storeLoginUrl(loginUrl));
    }

    // Do not reload the login page when user is navigated to the login page on SSO login error
    if (/^\/login.*error=/i.test(loginUrl)) {
        renderUI();
        return;
    }

    // Attempt a cookie re-login. Don't show UI until we know if login succeeded or failed
    store.dispatch(loginRequest({ callback: renderUI }));
})();
